import React, { useEffect, useRef, useState } from 'react';
import Base from '../Base';
import Title from '../../components/common/Title';
import { useTranslation } from 'react-i18next';
import { apiRequest, apiFormRequest, photoSrc, canSharePhotos, downloadPhoto, sharePhoto } from '../../utils';
import Card from '../../components/elements/Card';
import Icon from '../../components/common/Icon';
import Back from '../../components/common/Back';
import Button from '../../components/elements/Button';
import Modal from '../../components/elements/Modal';
import Toast from '../../components/forms/Toast';
import NotFound from '../../components/common/NotFound';
import TeamPaymentBanner, { useTeamPaymentLock } from '../../components/team/TeamPaymentLock';

// Free-form team album — members upload whatever photos they like. Totally
// separate from the scored picture missions: these are plain blobs in the
// public 'albums' container (no submission row, no review, no points).
const TeamPhotos = () => {
  const { t } = useTranslation();
  const [photos, setPhotos] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showToast, setShowToast] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [deletingName, setDeletingName] = useState(null);
  // The NAME of the picture in the preview rather than its URL: turning one re-reads the
  // album, and an address held here would go on pointing at the photo as it was before.
  const [selectedName, setSelectedName] = useState(null);
  const [rotatingName, setRotatingName] = useState(null);
  const [savingPhoto, setSavingPhoto] = useState(false);

  // A property of the browser, not of the photograph, so it is asked once.
  const [shareable] = useState(canSharePhotos);
  // No uploads to a team that is waiting on its (failed) online payment.
  const payLock = useTeamPaymentLock({ ownTeam: true });

  const cameraInputRef = useRef(null);
  const fileInputRef = useRef(null);

  const load = async () => {
    try {
      setLoading(true);
      const data = await apiRequest('GET', '/team-photos?own_team=1');
      setPhotos(data.photos || []);
    } catch (err) {
      setError(err.message || 'somethingWentWrong');
      setShowToast(true);
      setPhotos([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const uploadFiles = async (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = ''; // let the same file be re-picked later
    if (!files.length) return;

    setUploading(true);
    setError(null);
    let failed = 0;
    // One request per file so the backend assigns each its own (N) counter.
    for (const file of files) {
      const formData = new FormData();
      formData.append('file', file);
      try {
        await apiFormRequest('/team-photos', formData);
      } catch {
        failed += 1;
      }
    }
    setUploading(false);
    setError(failed ? 'somethingWentWrong' : 'photoUploaded');
    setShowToast(true);
    load();
  };

  const deletePhoto = async (name) => {
    if (!window.confirm(t('confirmDeletePhoto'))) return;
    try {
      setDeletingName(name);
      await apiRequest('DELETE', '/team-photos', { name });
      setPhotos((prev) => (prev || []).filter((p) => p.name !== name));
      setError('photoDeleted');
      setShowToast(true);
    } catch (err) {
      setError(err.message || 'somethingWentWrong');
      setShowToast(true);
    } finally {
      setDeletingName(null);
    }
  };

  // Straighten a photo that came off a phone sideways: a quarter clockwise, or -1 for the other
  // way. The turned picture is written back under the SAME blob name, so the album keeps its
  // order and every link already shared still works — what changes is the blob's date, and the
  // list is re-read for it because that date is what makes the browser fetch the turned one
  // rather than redraw the crooked picture it already has.
  const rotatePhoto = async (name, turns) => {
    try {
      setRotatingName(name);
      await apiRequest('POST', '/team-photos/rotate', { name, turns });
      await load();
    } catch (err) {
      setError(err.message || 'somethingWentWrong');
      setShowToast(true);
    } finally {
      setRotatingName(null);
    }
  };

  // The picture the preview is showing, looked up fresh on every render: after a turn the album
  // is re-read, and this picks up the new address without anything else here having to know.
  const selected = selectedName ? (photos || []).find((p) => p.name === selectedName) : null;

  // Save the picture, or hand it to the phone's share sheet. Deliberately fed the SAME address the
  // preview is drawing (photoSrc, ?v= and all) rather than the bare blob URL: after a turn those
  // are two different pictures, and the one somebody wants is the one they are looking at.
  const savePhoto = async (action) => {
    if (savingPhoto || !selected) return;
    setSavingPhoto(true);
    try {
      await action(photoSrc(selected), selected.name);
    } finally {
      setSavingPhoto(false);
    }
  };

  return (
    <Base activeNav="home">
      <Card>
        <Back to="/home" />
        <Title my="1" className="mt-3">
          <Icon name="camera" theme="primary" className="me-2" />
          {t('teamPhotos')}
        </Title>
        <p className="text-secondary mx-2 mb-3">{t('teamPhotosIntro')}</p>
        <TeamPaymentBanner lock={payLock} className="mx-2" />

        <Button onClick={() => cameraInputRef.current.click()} theme="secondary" disabled={uploading || !!payLock}>
          <Icon name="camera" theme="light" className="me-1" /> {t('openCamera')}
        </Button>
        <Button onClick={() => fileInputRef.current.click()} disabled={uploading || !!payLock}>
          <Icon name="photo-video" theme="light" className="me-1" />
          {uploading ? t('loading') : t('uploadFromGallery')}
        </Button>

        <input
          type="file"
          accept="image/*"
          capture="environment"
          ref={cameraInputRef}
          style={{ display: 'none' }}
          onChange={uploadFiles}
        />
        <input
          type="file"
          accept="image/*"
          multiple
          ref={fileInputRef}
          style={{ display: 'none' }}
          onChange={uploadFiles}
        />
      </Card>

      {photos && photos.length > 0 ? (
        <div className="row d-flex justify-content-center g-0">
          {photos.map((photo) => (
            <div className="col d-flex justify-content-center mb-3" key={photo.name}>
              <div style={{ position: 'relative', width: '11rem', height: '11rem' }}>
                <img
                  src={photoSrc(photo)}
                  alt=""
                  onClick={() => setSelectedName(photo.name)}
                  style={{
                    width: '100%',
                    height: '100%',
                    objectFit: 'cover',
                    borderRadius: '8px',
                    cursor: 'pointer',
                    backgroundColor: 'white',
                  }}
                  className="outline-light"
                />
                <button
                  type="button"
                  onClick={() => deletePhoto(photo.name)}
                  disabled={deletingName === photo.name}
                  aria-label={t('deletePhoto')}
                  style={{
                    position: 'absolute',
                    top: '6px',
                    right: '6px',
                    width: '2rem',
                    height: '2rem',
                    border: 'none',
                    borderRadius: '50%',
                    background: 'rgba(0, 0, 0, 0.55)',
                    color: '#fff',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    opacity: deletingName === photo.name ? 0.5 : 1,
                  }}
                >
                  <Icon name="trash" theme="light" />
                </button>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <NotFound translationKey="noTeamPhotos" loading={loading} />
      )}

      <Modal
        show={!!selected}
        title={t('photoPreview')}
        onClose={() => setSelectedName(null)}
        hideFooter={true}
      >
        {selected && (
          <div className="text-center">
            <img
              src={photoSrc(selected)}
              alt="Preview"
              className="img-fluid mb-3"
              style={{ maxHeight: '60vh', borderRadius: '8px' }}
            />
            {/* Turning is offered here rather than on the thumbnails: the tiles are cropped
                square, so a sideways picture only reads as sideways once it is open. */}
            <div className="d-flex justify-content-center flex-wrap gap-2">
              <Button
                theme="secondary"
                styles={{}}
                disabled={rotatingName === selected.name}
                onClick={() => rotatePhoto(selected.name, -1)}
              >
                <Icon name="rotate-left" theme="light" className="me-1" /> {t('rotateLeft')}
              </Button>
              <Button
                theme="secondary"
                styles={{}}
                disabled={rotatingName === selected.name}
                onClick={() => rotatePhoto(selected.name, 1)}
              >
                <Icon name="rotate-right" theme="light" className="me-1" /> {t('rotateRight')}
              </Button>
              {/* Share where the browser offers it — on a phone that is the whole point, the photo
                  goes straight into WhatsApp instead of a downloads folder nobody opens. */}
              {shareable && (
                <Button styles={{}} disabled={savingPhoto} onClick={() => savePhoto(sharePhoto)}>
                  <Icon name="share" theme="light" className="me-1" /> {t('share')}
                </Button>
              )}
              <Button styles={{}} disabled={savingPhoto} onClick={() => savePhoto(downloadPhoto)}>
                <Icon name="download" theme="light" className="me-1" /> {t('download')}
              </Button>
            </div>
          </div>
        )}
      </Modal>

      <Toast showToast={showToast} setShowToast={setShowToast} message={t(error)} />
    </Base>
  );
};

export default TeamPhotos;
